import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { LogOut, ArrowLeft, Server, Store, Globe, Mail, Receipt, Moon, Sun } from "lucide-react";
import { API, LogoMark, getToken, getUser, saveSession, clearSession, authHeaders } from "@/lib/site";
import ServersTab from "@/components/portal/ServersTab";
import StoreTab from "@/components/portal/StoreTab";
import SitesTab from "@/components/portal/SitesTab";
import EmailTab from "@/components/portal/EmailTab";
import BillingTab from "@/components/portal/BillingTab";

const TABS = [
  { id: "servers", label: "My Servers", icon: Server },
  { id: "store", label: "Store", icon: Store },
  { id: "hosting", label: "Hosting", icon: Globe },
  { id: "email", label: "Email", icon: Mail },
  { id: "billing", label: "Billing", icon: Receipt },
];

export default function Portal() {
  const navigate = useNavigate();
  const [user, setUser] = useState(getUser());
  const [tab, setTab] = useState("servers");
  const [dark, setDark] = useState(() => localStorage.getItem("wynova-theme") === "dark");

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("wynova-theme", dark ? "dark" : "light");
  }, [dark]);

  useEffect(() => {
    if (!getToken()) {
      navigate("/auth");
      return;
    }
    axios.get(`${API}/auth/me`, { headers: authHeaders() })
      .then(({ data }) => { setUser(data); saveSession(getToken(), data); })
      .catch(() => { clearSession(); navigate("/auth"); });
  }, [navigate]);

  if (!user) return null;

  return (
    <div data-testid="portal-page" className="min-h-screen bg-slate-50 dark:bg-slate-950 text-foreground">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 dark:bg-slate-950/70 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <LogoMark className="w-8 h-8 shrink-0" />
            <span className="font-display font-bold tracking-tight truncate">Wynova<span className="text-primary"> Cloud</span></span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setDark((d) => !d)} data-testid="portal-theme-toggle" aria-label="Toggle theme"
                    className="p-2 rounded-full border border-slate-300 dark:border-slate-700 hover:border-primary hover:text-primary transition-colors">
              {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            <Link to="/" className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-slate-300 dark:border-slate-700 text-sm font-medium hover:border-primary hover:text-primary transition-colors">
              <ArrowLeft className="w-4 h-4" /> Website
            </Link>
            <span className="hidden md:inline text-xs text-slate-500 max-w-40 truncate" data-testid="portal-user-email">{user.email}</span>
            <button onClick={() => { clearSession(); navigate("/auth"); }} data-testid="portal-logout"
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-slate-900 dark:bg-white text-white dark:text-slate-900 text-sm font-medium hover:opacity-90">
              <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 flex gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`portal-tab-${t.id}`}
                    className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 whitespace-nowrap transition-colors ${
                      tab === t.id
                        ? "border-primary text-primary"
                        : "border-transparent text-slate-500 dark:text-slate-400 hover:text-foreground"
                    }`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </nav>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-10">
        <h1 className="font-display font-bold text-2xl sm:text-3xl tracking-tight mb-8">
          {TABS.find((t) => t.id === tab)?.label}
        </h1>
        {tab === "servers" && <ServersTab />}
        {tab === "store" && <StoreTab />}
        {tab === "hosting" && <SitesTab />}
        {tab === "email" && <EmailTab />}
        {tab === "billing" && <BillingTab />}
      </main>
    </div>
  );
}
